import { FGCustomInput, FGSelectInput } from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import { EJudgeType, SaveSubActivityJudgeDto, SubActivityGridDto, SubActivityJudgeApi } from "../../../../../api";
import { FGSRSHDateMaskInput, SmallFormGenerator } from "../../../../../components";
import { useApiService, useSaveApi, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

export interface IPersonSubActivityJudgeSavePanelProps {
  onSaved: () => void;
  subActivity: SubActivityGridDto;
  personId: number;
}

export const PersonSubActivityJudgeSavePanel: React.FunctionComponent<IPersonSubActivityJudgeSavePanelProps> = ({
  onSaved,
  subActivity,
  personId,
}) => {
  const { t, tUnsafe } = useTl();
  const api = useApiService(SubActivityJudgeApi);
  const [judgeType, judgeTypeLoading] = useReferential((a) => a.getJudgeType(), false);
  const [judgeTypeItems, setJudgeTypeItems] = React.useState([]);

  const { saving, saveItem } = useSaveApi<SaveSubActivityJudgeDto>(
    React.useMemo(
      () => ({
        saveApiFn: (data) => api.saveSubActivityJudge(data),
        onSaveFn: () => onSaved(),
      }),
      [api, onSaved]
    )
  );
  const initialData = React.useMemo(
    () =>
      new SaveSubActivityJudgeDto({
        subActivityId: subActivity.subActivityId,
        personId: personId,
      }),
    [subActivity.subActivityId, personId]
  );

  React.useEffect(() => {
    if (!judgeTypeLoading && judgeType) {
      const items = judgeType.map((c) => {
        return { label: tUnsafe(c.label), value: c.value };
      });
      setJudgeTypeItems(items);
    }
  }, [judgeType, judgeTypeLoading, tUnsafe]);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      judgeType: Yup.string().nullable().required(t(ETLCodes.Required)).oneOf(Object.keys(EJudgeType)),
      internshipDate: Yup.date()
        .nullable()
        .when("judgeType", (value, schema) =>
          value === EJudgeType.Internship ? schema.required(t(ETLCodes.Required)) : schema.nullable()
        ),
      nominationDate: Yup.date()
        .nullable()
        .when("judgeType", (value, schema) =>
          value === EJudgeType.Nominated ? schema.required(t(ETLCodes.Required)) : schema.nullable()
        ),
      excludedFrom: Yup.date().nullable(),
      excludedTo: Yup.date()
        .nullable()
        .when("excludedFrom", (value, schema) => (value ? schema.required(t(ETLCodes.Required)) : schema.nullable())),
    });
  }, [t]);

  return (
    <SmallFormGenerator
      onSubmit={saveItem}
      initialValues={initialData}
      editMode={true}
      saving={saving}
      validationSchema={FormSchema}
    >
      <FGCustomInput name="subActivity" label={t(ETLCodes.SubActivity)}>
        {(ctx) => (
          <>
            {subActivity.activity} - {subActivity.name}
          </>
        )}
      </FGCustomInput>
      <FGSelectInput name="judgeType" label={t(ETLCodes.JudgeType)} items={judgeTypeItems} loading={judgeTypeLoading} />
      <FGSRSHDateMaskInput name="internshipDate" label={t(ETLCodes.JudgeDateInternship)} />
      <FGSRSHDateMaskInput name="nominationDate" label={t(ETLCodes.JudgeDateNomination)} />
      <FGSRSHDateMaskInput name="excludedFrom" label={t(ETLCodes.JudgeDateExcludedFrom)} />
      <FGSRSHDateMaskInput name="excludedTo" label={t(ETLCodes.JudgeDateExcludedTo)} />
    </SmallFormGenerator>
  );
};
